import React, { FC } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import i18n from 'i18n-js';
import { scale } from '@components/frame';
import { font } from '@styles/fonts';
import ColorWrapper from '@styles/colors';
import ModalWrapper from '@components/modal/ModalWrapper';
import { SmallLoading } from '@components/loading';
import { SearchItemType } from '@interfaces/base';
import { ColorFunction } from '@styles/colors/types';
import { useManageAttachConfirmationModal } from './hooks/useManageAttachConfirmationModal';

export interface AttachConfirmationModalProps {
  visible: boolean;
  onClose: () => void;
  onConfirm: () => void;
  itemName: string;
  itemType: SearchItemType;
  C: ColorFunction;
  updateInProgress: boolean;
}

const AttachConfirmationModal: FC<AttachConfirmationModalProps> = ({ visible, onClose, onConfirm, itemName, itemType, C, updateInProgress }) => {
  console.log('AttachConfirmationModal render:', { visible, itemName, itemType, onConfirm: typeof onConfirm });

  const { typeText } = useManageAttachConfirmationModal({ itemType });

  return (
    <ModalWrapper isVisible={visible} onBackdropPress={onClose} C={C} backdropColor="Overlay/Light/Dim for modal">
      <View
        style={{
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
          paddingHorizontal: scale(40),
        }}
      >
        <View
          style={{
            backgroundColor: C('Background/Light/Modal'),
            borderRadius: scale(14),
            width: '100%',
            maxWidth: scale(300),
            overflow: 'hidden',
          }}
        >
          {updateInProgress && (
            <View style={{ height: scale(200), justifyContent: 'center', alignItems: 'center' }}>
              <SmallLoading />
            </View>
          )}
          {!updateInProgress && (
            <>
              {/* Header and content */}
              <View
                style={{
                  paddingHorizontal: scale(20),
                  paddingTop: scale(20),
                  paddingBottom: scale(16),
                  alignItems: 'center',
                }}
              >
                <Text
                  style={[
                    font['Headline'],
                    {
                      fontSize: scale(17),
                      fontWeight: '600',
                      color: C('Label/Light/Primary'),
                      marginBottom: scale(8),
                      textAlign: 'center',
                    },
                  ]}
                >
                  {i18n.t('Attach') + ' ' + typeText + '?'}
                </Text>
                <Text
                  style={[
                    font['Footnote'],
                    {
                      fontSize: scale(13),
                      fontWeight: '400',
                      color: C('Label/Light/Secondary'),
                      textAlign: 'center',
                      lineHeight: scale(18),
                    },
                  ]}
                >
                  {i18n.t('This will attach {{itemName}} to this image. You can remove the attachment later if needed.', { itemName })}
                </Text>
              </View>

              {/* Separator */}
              <View
                style={{
                  height: StyleSheet.hairlineWidth,
                  backgroundColor: C('Hairline/Light/Secondary'),
                }}
              />

              {/* Attach button */}
              <TouchableOpacity
                onPress={() => {
                  console.log('Attach button pressed, calling onConfirm');
                  onConfirm && onConfirm();
                }}
                activeOpacity={0.8}
                style={{
                  paddingVertical: scale(16),
                  alignItems: 'center',
                }}
              >
                <Text
                  style={[
                    font['Body'],
                    {
                      fontSize: scale(17),
                      fontWeight: '400',
                      color: C('Link/Light/Primary'),
                      textAlign: 'center',
                    },
                  ]}
                >
                  {i18n.t('Attach')}
                </Text>
              </TouchableOpacity>

              {/* Separator */}
              <View
                style={{
                  height: StyleSheet.hairlineWidth,
                  backgroundColor: C('Hairline/Light/No trans [mobile]'),
                }}
              />

              {/* Cancel button */}
              <TouchableOpacity
                onPress={() => {
                  console.log('Cancel button pressed, calling onClose');
                  onClose && onClose();
                }}
                activeOpacity={0.8}
                style={{
                  paddingVertical: scale(16),
                  alignItems: 'center',
                }}
              >
                <Text
                  style={[
                    font['Body'],
                    {
                      fontSize: scale(17),
                      fontWeight: '600',
                      color: C('Label/Light/Warning'),
                      textAlign: 'center',
                    },
                  ]}
                >
                  {i18n.t('Cancel')}
                </Text>
              </TouchableOpacity>
            </>
          )}
        </View>
      </View>
    </ModalWrapper>
  );
};

export default ColorWrapper(AttachConfirmationModal);
